import React from 'react';
import Icon from '../../../components/AppIcon';

// ─── Hebrew Grammar Code Parsers ─────────────────────────────────────────────
// Format: Language:PartOfSpeech-Gender-Number-State  (e.g. H:Ncmpa, HNcmpa, HVhrmsa)
// The leading "H:" or "H" prefix indicates Hebrew/Aramaic

const HEBREW_PARTS_OF_SPEECH = {
  N: 'Noun',
  V: 'Verb',
  A: 'Adjective',
  D: 'Adverb',
  Prep: 'Preposition',
  Conj: 'Conjunction',
  Art: 'Article',
  Pron: 'Pronoun',
  Interj: 'Interjection',
  Neg: 'Negation particle',
  Exclam: 'Exclamation',
  Suf: 'Suffix',
  Infix: 'Infix',
  Prt: 'Particle',
  T: 'Title',
  // Single-letter abbreviations used in compact codes
  n: 'Noun',
  v: 'Verb',
  a: 'Adjective',
  d: 'Adverb',
  p: 'Preposition',
  c: 'Conjunction',
  r: 'Pronoun',
  x: 'Particle',
  i: 'Interjection',
};

const HEBREW_NOUN_TYPES = {
  c: 'common',
  p: 'proper',
  g: 'gentilic',
  t: 'title',
};

const HEBREW_GENDERS = {
  m: 'masculine',
  f: 'feminine',
  c: 'common (either gender)',
  b: 'both genders',
};

const HEBREW_NUMBERS = {
  s: 'singular',
  p: 'plural',
  d: 'dual',
};

const HEBREW_STATES = {
  a: 'absolute',
  c: 'construct',
  d: 'determined / definite',
};

const HEBREW_VERB_STEMS = {
  q: 'Qal (simple active)',
  N: 'Niphal (simple passive/reflexive)',
  p: 'Piel (intensive active)',
  P: 'Pual (intensive passive)',
  h: 'Hiphil (causative active)',
  H: 'Hophal (causative passive)',
  t: 'Hithpael (reflexive/reciprocal)',
  o: 'Polel',
  O: 'Polal',
  r: 'Hithpolel',
  m: 'Poel',
  M: 'Poal',
  k: 'Palel',
  K: 'Pulal',
  Q: 'Qal passive',
  l: 'Pilpel',
  L: 'Polpal',
  f: 'Hithpalpel',
  D: 'Nithpael',
  j: 'Pealal',
  i: 'Pilel',
  u: 'Hothpaal',
  c: 'Tiphil',
  v: 'Hishtaphel',
  w: 'Nithpalel',
  y: 'Nithpoel',
  z: 'Hithpoel',
};

const HEBREW_VERB_ASPECTS = {
  p: 'perfect (completed action)',
  i: 'imperfect (incomplete/ongoing action)',
  w: 'waw-consecutive imperfect',
  q: 'waw-consecutive perfect',
  j: 'jussive (wish/command)',
  v: 'imperative (command)',
  c: 'cohortative (strong wish)',
  a: 'infinitive absolute',
  r: 'infinitive construct',
  s: 'participle active',
  o: 'participle passive',
};

const HEBREW_PERSONS = {
  1: '1st person',
  2: '2nd person',
  3: '3rd person',
};

const HEBREW_LANGUAGE_PREFIXES = {
  H: 'Hebrew',
  A: 'Aramaic',
  N: 'Name (proper noun)',
};

// ─── Greek Grammar Code Parsers ───────────────────────────────────────────────
// Format: PartOfSpeech-Case-Number-Gender  (e.g. N-NSM, V-AAI-3S, PREP)
// See: https://www.blueletterbible.org/resources/morphology.cfm

const GREEK_PARTS_OF_SPEECH = {
  N: 'Noun',
  V: 'Verb',
  'V-': 'Verb',
  ADJ: 'Adjective',
  ADV: 'Adverb',
  PREP: 'Preposition',
  CONJ: 'Conjunction',
  COND: 'Conditional particle',
  PRT: 'Particle',
  PRTCL: 'Particle',
  INJ: 'Interjection',
  ARAM: 'Aramaic transliteration',
  HEB: 'Hebrew transliteration',
  R: 'Relative pronoun',
  C: 'Reciprocal pronoun',
  D: 'Demonstrative pronoun',
  T: 'Definite article',
  K: 'Correlative pronoun',
  I: 'Interrogative pronoun',
  X: 'Indefinite pronoun',
  Q: 'Correlative or interrogative pronoun',
  F: 'Reflexive pronoun',
  S: 'Possessive pronoun',
  P: 'Personal pronoun',
};

const GREEK_CASES = {
  N: 'nominative (subject)',
  G: 'genitive (possession/of)',
  D: 'dative (indirect object/to/for)',
  A: 'accusative (direct object)',
  V: 'vocative (direct address)',
};

const GREEK_NUMBERS = {
  S: 'singular',
  P: 'plural',
};

const GREEK_GENDERS = {
  M: 'masculine',
  F: 'feminine',
  N: 'neuter',
};

const GREEK_VERB_TENSES = {
  P: 'present',
  I: 'imperfect',
  F: 'future',
  A: 'aorist (simple past)',
  X: 'perfect',
  Y: 'pluperfect',
};

const GREEK_VERB_VOICES = {
  A: 'active (subject performs action)',
  M: 'middle (subject acts on itself)',
  P: 'passive (subject receives action)',
  E: 'middle or passive',
  D: 'middle deponent',
  O: 'passive deponent',
  N: 'middle or passive deponent',
  Q: 'impersonal active',
};

const GREEK_VERB_MOODS = {
  I: 'indicative (statement of fact)',
  S: 'subjunctive (possibility/wish)',
  O: 'optative (wish/prayer)',
  M: 'imperative (command)',
  N: 'infinitive (verbal noun)',
  P: 'participle (verbal adjective)',
};

const GREEK_PERSONS = {
  1: '1st person',
  2: '2nd person',
  3: '3rd person',
};

// ─── Parse Hebrew Grammar Code ────────────────────────────────────────────────

function parseHebrewGrammar(code) {
  if (!code) return null;

  const parts = [];
  let remaining = code?.trim();

  // Detect language prefix (H: or A: or N:)
  let langPrefix = 'H';
  if (/^[HAN]:/?.test(remaining)) {
    langPrefix = remaining?.[0];
    remaining = remaining?.slice(2);
  } else if (/^[HAN]/?.test(remaining)) {
    langPrefix = remaining?.[0];
    remaining = remaining?.slice(1);
  }

  const langName = HEBREW_LANGUAGE_PREFIXES?.[langPrefix] || 'Hebrew';
  parts?.push({ label: 'Language', value: langName });

  // Try to identify part of speech from the start of remaining
  // Common patterns: Nc, Np, V, A, D, Prep, Conj, Art, Pron, T
  let pos = '';
  let rest = remaining;

  // Multi-char POS first
  const multiPOS = ['Prep', 'Conj', 'Art', 'Pron', 'Interj', 'Neg', 'Exclam', 'Suf', 'Infix', 'Prt'];
  let foundMulti = false;
  for (const mp of multiPOS) {
    if (rest?.startsWith(mp)) {
      pos = mp;
      rest = rest?.slice(mp?.length);
      foundMulti = true;
      break;
    }
  }

  if (!foundMulti && rest?.length > 0) {
    // Single char POS
    const firstChar = rest?.[0];
    if ('NVADTnvadpcrxi'?.includes(firstChar)) {
      pos = firstChar;
      rest = rest?.slice(1);
    }
  }

  if (pos) {
    let posName = HEBREW_PARTS_OF_SPEECH?.[pos] || pos;
    parts?.push({ label: 'Part of Speech', value: posName });
  }

  // For verbs: stem + aspect + person + gender + number
  if (pos === 'V' || pos === 'v') {
    // Verb stem (first char of rest)
    if (rest?.length > 0) {
      const stem = rest?.[0];
      const stemName = HEBREW_VERB_STEMS?.[stem];
      if (stemName) {
        parts?.push({ label: 'Verb Stem', value: stemName });
        rest = rest?.slice(1);
      }
    }
    // Verb aspect
    if (rest?.length > 0) {
      const aspect = rest?.[0];
      const aspectName = HEBREW_VERB_ASPECTS?.[aspect];
      if (aspectName) {
        parts?.push({ label: 'Aspect / Form', value: aspectName });
        rest = rest?.slice(1);
      }
    }
    // Person
    if (rest?.length > 0 && /[123]/?.test(rest?.[0])) {
      parts?.push({ label: 'Person', value: HEBREW_PERSONS?.[rest?.[0]] || rest?.[0] });
      rest = rest?.slice(1);
    }
    // Gender
    if (rest?.length > 0 && 'mfcb'?.includes(rest?.[0])) {
      parts?.push({ label: 'Gender', value: HEBREW_GENDERS?.[rest?.[0]] || rest?.[0] });
      rest = rest?.slice(1);
    }
    // Number
    if (rest?.length > 0 && 'spd'?.includes(rest?.[0])) {
      parts?.push({ label: 'Number', value: HEBREW_NUMBERS?.[rest?.[0]] || rest?.[0] });
      rest = rest?.slice(1);
    }
  } else if (pos === 'N' || pos === 'n') {
    // Noun type (c=common, p=proper, g=gentilic, t=title)
    if (rest?.length > 0 && 'cpgt'?.includes(rest?.[0])) {
      parts?.push({ label: 'Noun Type', value: HEBREW_NOUN_TYPES?.[rest?.[0]] || rest?.[0] });
      rest = rest?.slice(1);
    }
    // Gender
    if (rest?.length > 0 && 'mfcb'?.includes(rest?.[0])) {
      parts?.push({ label: 'Gender', value: HEBREW_GENDERS?.[rest?.[0]] || rest?.[0] });
      rest = rest?.slice(1);
    }
    // Number
    if (rest?.length > 0 && 'spd'?.includes(rest?.[0])) {
      parts?.push({ label: 'Number', value: HEBREW_NUMBERS?.[rest?.[0]] || rest?.[0] });
      rest = rest?.slice(1);
    }
    // State
    if (rest?.length > 0 && 'acd'?.includes(rest?.[0])) {
      parts?.push({ label: 'State', value: HEBREW_STATES?.[rest?.[0]] || rest?.[0] });
      rest = rest?.slice(1);
    }
  } else {
    // For other POS, try gender/number/state
    if (rest?.length > 0 && 'mfcb'?.includes(rest?.[0])) {
      parts?.push({ label: 'Gender', value: HEBREW_GENDERS?.[rest?.[0]] || rest?.[0] });
      rest = rest?.slice(1);
    }
    if (rest?.length > 0 && 'spd'?.includes(rest?.[0])) {
      parts?.push({ label: 'Number', value: HEBREW_NUMBERS?.[rest?.[0]] || rest?.[0] });
      rest = rest?.slice(1);
    }
    if (rest?.length > 0 && 'acd'?.includes(rest?.[0])) {
      parts?.push({ label: 'State', value: HEBREW_STATES?.[rest?.[0]] || rest?.[0] });
    }
  }

  return parts;
}

// ─── Parse Greek Grammar Code ─────────────────────────────────────────────────
// Formats: "N-NSM-P", "V-AAI-3S", "PREP", "T-NSM", "ADJ-NSM"

function parseGreekGrammar(code) {
  if (!code) return null;

  const parts = [];
  const segments = code?.trim()?.split('-');

  if (segments?.length === 0) return null;

  const posCode = segments?.[0]?.toUpperCase();

  // Identify part of speech
  let posName = GREEK_PARTS_OF_SPEECH?.[posCode];

  // Handle pronoun types (P, R, C, D, T, K, I, X, Q, F, S)
  if (!posName && posCode?.length === 1) {
    posName = GREEK_PARTS_OF_SPEECH?.[posCode] || 'Pronoun';
  }

  if (posName) {
    parts?.push({ label: 'Part of Speech', value: posName });
  } else {
    parts?.push({ label: 'Part of Speech', value: posCode });
  }

  // No further segments — standalone word like PREP, CONJ, ADV
  if (segments?.length === 1) {
    return parts;
  }

  // Second segment: for nouns/adjectives/pronouns/articles = Case+Number+Gender
  // For verbs = Tense+Voice+Mood
  const seg2 = segments?.[1];

  const isVerb = posCode === 'V' || posCode?.startsWith('V-');

  if (isVerb && seg2?.length >= 3) {
    // Tense (1st char)
    const tense = GREEK_VERB_TENSES?.[seg2?.[0]];
    if (tense) parts?.push({ label: 'Tense', value: tense });
    // Voice (2nd char)
    const voice = GREEK_VERB_VOICES?.[seg2?.[1]];
    if (voice) parts?.push({ label: 'Voice', value: voice });
    // Mood (3rd char)
    const mood = GREEK_VERB_MOODS?.[seg2?.[2]];
    if (mood) parts?.push({ label: 'Mood', value: mood });
  } else if (seg2?.length >= 2) {
    // Case (1st char)
    const caseVal = GREEK_CASES?.[seg2?.[0]];
    if (caseVal) parts?.push({ label: 'Case', value: caseVal });
    // Number (2nd char)
    const num = GREEK_NUMBERS?.[seg2?.[1]];
    if (num) parts?.push({ label: 'Number', value: num });
    // Gender (3rd char, optional)
    if (seg2?.length >= 3) {
      const gender = GREEK_GENDERS?.[seg2?.[2]];
      if (gender) parts?.push({ label: 'Gender', value: gender });
    }
  }

  // Third segment: for verbs = Person+Number (e.g. "3S" = 3rd singular)
  if (segments?.length >= 3) {
    const seg3 = segments?.[2];
    if (/^[123]/?.test(seg3)) {
      const person = GREEK_PERSONS?.[seg3?.[0]];
      if (person) parts?.push({ label: 'Person', value: person });
      if (seg3?.length >= 2) {
        const num = GREEK_NUMBERS?.[seg3?.[1]];
        if (num) parts?.push({ label: 'Number', value: num });
      }
    } else if (seg3 === 'P') {
      parts?.push({ label: 'Note', value: 'Proper name' });
    }
  }

  return parts;
}

// ─── Detect Language from Grammar Code ───────────────────────────────────────

function detectLanguage(code) {
  if (!code) return 'unknown';
  const c = code?.trim();
  // Hebrew/Aramaic codes start with H, A, N followed by : or a letter
  if (/^[HAN]:/?.test(c) || /^[HAN][NVADTnvadpcrxi]/?.test(c)) return 'hebrew';
  // Greek codes use uppercase dash-separated format
  if (/^[NVTPRCDFSKIQX](-|$)/?.test(c) || /^(ADJ|ADV|PREP|CONJ|COND|PRT|PRTCL|INJ|ARAM|HEB)/?.test(c)) return 'greek';
  // Fallback: if it contains a dash it's likely Greek
  if (c?.includes('-')) return 'greek';
  return 'hebrew';
}

// ─── Plain-English Summary ────────────────────────────────────────────────────

function buildSummary(parsedParts, code, language) {
  if (!parsedParts || parsedParts?.length === 0) return null;

  const get = (label) => parsedParts?.find(p => p?.label === label)?.value;

  let pos = get('Part of Speech') || get('Language');
  const lang = get('Language') || (language === 'hebrew' ? 'Hebrew' : 'Greek');

  if (language === 'hebrew') {
    const stem = get('Verb Stem');
    const aspect = get('Aspect / Form');
    const person = get('Person');
    const gender = get('Gender');
    const number = get('Number');
    const state = get('State');
    const nounType = get('Noun Type');

    let summary = `This is a ${lang} `;
    if (pos) summary += pos?.toLowerCase();
    if (nounType) summary += ` (${nounType})`;
    if (stem) summary += ` in the ${stem} stem`;
    if (aspect) summary += `, ${aspect} form`;
    if (person) summary += `, ${person}`;
    if (gender) summary += `, ${gender}`;
    if (number) summary += `, ${number}`;
    if (state) summary += `, ${state} state`;
    summary += '.';
    return summary;
  } else {
    const tense = get('Tense');
    const voice = get('Voice');
    const mood = get('Mood');
    const caseVal = get('Case');
    const number = get('Number');
    const gender = get('Gender');
    const person = get('Person');

    let summary = `This is a Greek `;
    if (pos) summary += pos?.toLowerCase();
    if (tense) summary += `, ${tense} tense`;
    if (voice) summary += `, ${voice} voice`;
    if (mood) summary += `, ${mood} mood`;
    if (person) summary += `, ${person}`;
    if (caseVal) summary += `, ${caseVal} case`;
    if (gender) summary += `, ${gender}`;
    if (number) summary += `, ${number}`;
    summary += '.';
    return summary;
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

const GrammarModal = ({ grammarCode, onClose }) => {
  if (!grammarCode) return null;

  const language = detectLanguage(grammarCode);
  const isHebrew = language === 'hebrew';
  const parsedParts = isHebrew
    ? parseHebrewGrammar(grammarCode)
    : parseGreekGrammar(grammarCode);

  let summary = buildSummary(parsedParts, grammarCode, language);

  const accentColor = isHebrew ? 'violet' : 'blue';
  const accentClasses = {
    dot: isHebrew ? 'bg-violet-500' : 'bg-blue-500',
    label: isHebrew ? 'text-violet-600' : 'text-blue-600',
    badge: isHebrew ? 'bg-violet-100 text-violet-700' : 'bg-blue-100 text-blue-700',
    summaryBg: isHebrew ? 'bg-violet-50 border-violet-200 text-violet-900' : 'bg-blue-50 border-blue-200 text-blue-900',
    rowBg: isHebrew ? 'bg-violet-50/40' : 'bg-blue-50/40',
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/40"
        onClick={onClose}
      />
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col pointer-events-auto">

          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
            <div className="flex items-center gap-2 flex-wrap">
              <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${accentClasses?.dot}`} />
              <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                {isHebrew ? 'Hebrew' : 'Greek'} Grammar
              </span>
              <span className={`ml-1 text-base font-bold font-mono ${accentClasses?.label}`}>
                {grammarCode}
              </span>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-700 transition-colors rounded-full p-1 hover:bg-gray-100 flex-shrink-0"
              aria-label="Close grammar explanation"
            >
              <Icon name="X" size={18} />
            </button>
          </div>

          {/* Body */}
          <div className="overflow-y-auto flex-1 px-5 py-4">

            {/* Plain-English Summary */}
            {summary && (
              <div className={`mb-4 p-3 rounded-lg border text-sm leading-relaxed font-medium ${accentClasses?.summaryBg}`}>
                <div className="flex gap-2">
                  <Icon name="BookOpen" size={15} className="flex-shrink-0 mt-0.5 opacity-70" />
                  <span>{summary}</span>
                </div>
              </div>
            )}

            {/* Breakdown Table */}
            {parsedParts && parsedParts?.length > 0 ? (
              <div className="space-y-1">
                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">
                  Code Breakdown
                </p>
                {parsedParts?.map((part, idx) => (
                  <div
                    key={idx}
                    className={`flex items-center justify-between px-3 py-2 rounded-lg ${
                      idx % 2 === 0 ? 'bg-gray-50' : accentClasses?.rowBg
                    }`}
                  >
                    <span className="text-xs font-semibold text-gray-500 w-36 flex-shrink-0">
                      {part?.label}
                    </span>
                    <span className={`text-xs font-medium flex-1 text-right ${accentClasses?.label}`}>
                      {part?.value}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2 py-8 text-gray-400">
                <Icon name="HelpCircle" size={24} />
                <span className="text-sm text-center">
                  Could not parse grammar code <strong className="font-mono">{grammarCode}</strong>.
                </span>
                <span className="text-xs text-center text-gray-400 max-w-xs">
                  This may be an unusual or abbreviated code. Refer to STEPBible documentation for details.
                </span>
              </div>
            )}

            {/* Grammar System Explanation */}
            <div className="mt-5 p-3 rounded-lg bg-gray-50 border border-gray-200">
              <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                About Grammar Codes
              </p>
              {isHebrew ? (
                <p className="text-xs text-gray-600 leading-relaxed">
                  Hebrew grammar codes follow the pattern <span className="font-mono bg-gray-200 px-1 rounded">Language:PartOfSpeech-Gender-Number-State</span>.
                  The language prefix is <strong>H</strong> (Hebrew) or <strong>A</strong> (Aramaic).
                  Verb codes include the stem (e.g. Qal, Niphal, Hiphil) and aspect (perfect, imperfect, participle, etc.).
                  Noun codes include type (common/proper), gender, number, and state (absolute/construct).
                </p>
              ) : (
                <p className="text-xs text-gray-600 leading-relaxed">
                  Greek grammar codes follow the pattern <span className="font-mono bg-gray-200 px-1 rounded">PartOfSpeech-Case/Tense-Number/Voice-Gender/Mood</span>.
                  Noun codes include case (nominative, genitive, dative, accusative), number, and gender.
                  Verb codes include tense (present, aorist, perfect, etc.), voice (active, middle, passive), and mood (indicative, subjunctive, imperative, etc.).
                </p>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 rounded-b-xl">
            <p className="text-[10px] text-gray-400">
              Grammar data: STEPBible.org / Tyndale House Cambridge (CC BY 4.0)
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default GrammarModal;
